import { useCallback, useEffect, useRef, useState } from 'react';

// Kapselt einen laufenden LLM-Stream: Text + done/error, AbortController und Source-Events.
export default function useLlmStream() {
  const [text, setText] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState(null);
  const [sources, setSources] = useState([]);
  const [meta, setMeta] = useState(null);
  const abortRef = useRef(null);

  useEffect(() => () => abortRef.current?.abort(), []);

  const reset = useCallback(() => {
    setText('');
    setError(null);
    setSources([]);
    setMeta(null);
  }, []);

  const run = useCallback(async (runner) => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setIsStreaming(true);
    setError(null);
    setText('');
    setSources([]);
    setMeta(null);
    try {
      await runner({
        signal: controller.signal,
        onEvent: (evt) => {
          if (evt.error) {
            setError(evt.error);
            return;
          }
          if (evt.sources) {
            setSources(evt.sources);
            return;
          }
          if (evt.function || evt.mode || evt.transcript_chars !== undefined) {
            setMeta((prev) => ({
              ...(prev || {}),
              ...(evt.function ? { function: evt.function } : {}),
              ...(evt.mode ? { mode: evt.mode } : {}),
              ...(evt.transcript_chars !== undefined
                ? { transcriptChars: evt.transcript_chars }
                : {}),
              ...(evt.context_chunks !== undefined
                ? { contextChunks: evt.context_chunks }
                : {}),
              ...(evt.num_ctx !== undefined ? { numCtx: evt.num_ctx } : {}),
            }));
            return;
          }
          if (evt.chunk) {
            setText((prev) => prev + evt.chunk);
            return;
          }
          if (evt.done) {
            // no-op, das Ende handlen wir über das Resolve der Promise
          }
        },
      });
    } catch (e) {
      if (e.name !== 'AbortError') {
        setError(e.message || String(e));
      }
    } finally {
      setIsStreaming(false);
    }
  }, []);

  const abort = useCallback(() => {
    abortRef.current?.abort();
  }, []);

  return {
    text,
    isStreaming,
    error,
    sources,
    meta,
    run,
    abort,
    reset,
    setText,
    setMeta,
  };
}
